const yaml = require('js-yaml');

/**
 * Keys that must never be copied onto the parsed result, because assigning them
 * can pollute Object.prototype (prototype-pollution).
 */
const DANGEROUS_KEYS = ['__proto__', 'constructor', 'prototype'];

/**
 * Whether a value is safe to surface to the UI as inert metadata.
 *
 * We only keep primitives (and arrays of primitives). Nested objects, functions
 * and class instances are dropped — frontmatter metadata is flat by design, and
 * dropping anything richer keeps the result inert.
 *
 * @param {*} value - Candidate value parsed from YAML.
 * @returns {boolean} True when the value is a string, number, boolean or an
 *   array of those.
 */
const isInertValue = (value) => {
  if (value === null) return true;
  const type = typeof value;
  if (type === 'string' || type === 'number' || type === 'boolean') return true;
  if (Array.isArray(value)) {
    return value.every((item) => {
      const itemType = typeof item;
      return item === null || itemType === 'string' || itemType === 'number' || itemType === 'boolean';
    });
  }
  return false;
};

/**
 * Parse markdown YAML frontmatter into a safe, inert metadata object.
 *
 * ## Trust boundary
 * Today the only frontmatter parsed by this app comes from TRUSTED sources:
 * - bundled markdown in `src/content/*.md` (committed to the repo, built in), and
 * - hardcoded lesson descriptions in `src/store/reducer.js`.
 *
 * This parser nonetheless treats the input as UNTRUSTED so that the trust
 * boundary can move (e.g. CMS- or API-supplied content) without reopening a
 * deserialization hole. It is the single, safe entry point for parsing
 * frontmatter — call sites must use it instead of `js-yaml`'s `load` directly.
 *
 * ## Why this is safe (AIKIDO-BE-004)
 * - Parses with an explicit `JSON_SCHEMA`, so only plain JSON-shaped data is
 *   produced: no custom YAML tags, no functions, no class instantiation.
 * - Any parse error (malformed YAML, unknown/custom tags such as
 *   `!!js/function` or `!!python/object/apply`) is caught and treated as "no
 *   metadata" (returns `{}`) rather than throwing or executing.
 * - Non-object results (a bare string, number, sequence, `null`) are rejected.
 * - The result is rebuilt key-by-key, skipping prototype-pollution keys
 *   (`__proto__`, `constructor`, `prototype`) and any non-primitive value, so
 *   the returned object is always a flat, inert bag of primitives.
 *
 * ## Expected shape
 * Frontmatter is page SEO/metadata. Known keys: `title`, `titleLine1`,
 * `titleLine2`, `seoTitle`, `description`, `intro`, `canonical`, `coverImage`.
 * Unknown keys are preserved only if their value is inert (primitive); they are
 * never used to drive control flow.
 *
 * @param {string} raw - Raw YAML frontmatter string (e.g. `yamlContent.value`).
 * @returns {Object} A flat object of primitive metadata values. Always returns
 *   a plain object — `{}` when input is missing, malformed, malicious, or not a
 *   YAML mapping.
 *
 * @example
 * parseFrontmatter('title: "Hello"\nseoTitle: "Hi"');
 * // { title: "Hello", seoTitle: "Hi" }
 *
 * @example
 * // Malicious / unexpected input is treated as inert:
 * parseFrontmatter('!!js/function "function(){return 1}"'); // {}
 * parseFrontmatter('- just\n- a\n- list');                  // {}
 */
const parseFrontmatter = (raw) => {
  if (typeof raw !== 'string') return {};

  let parsed;
  try {
    parsed = yaml.load(raw, { schema: yaml.JSON_SCHEMA });
  } catch (err) {
    return {};
  }

  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {};
  }

  const safe = {};
  for (const key of Object.keys(parsed)) {
    if (DANGEROUS_KEYS.includes(key)) continue;
    const value = parsed[key];
    if (isInertValue(value)) {
      safe[key] = value;
    }
  }
  return safe;
};

/**
 * Matches a YAML frontmatter block delimited by `---` at the very start of a
 * markdown document (an optional leading BOM is tolerated). The YAML body is
 * captured in group 1.
 */
const FRONTMATTER_BLOCK = /^\uFEFF?---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/;

/**
 * Extract and safely parse the leading YAML frontmatter block from a full
 * markdown document.
 *
 * This is the entry point for call sites that hold the whole markdown string
 * (e.g. fetched `.md` content) rather than just the frontmatter. Only a block
 * delimited by `---` at the very start of the document is considered; anything
 * else is ignored. The captured YAML body is parsed by {@link parseFrontmatter},
 * so the same safe-schema and inert-value guarantees apply (see AIKIDO-BE-004
 * notes above).
 *
 * @param {string} markdown - Full markdown document, possibly with frontmatter.
 * @returns {Object} A flat object of primitive metadata. Returns `{}` when there
 *   is no leading frontmatter block, or it is malformed, malicious, or not a
 *   YAML mapping.
 */
const parseMarkdownFrontmatter = (markdown) => {
  if (typeof markdown !== 'string') return {};
  const match = markdown.match(FRONTMATTER_BLOCK);
  if (!match) return {};
  return parseFrontmatter(match[1]);
};

module.exports = { parseFrontmatter, parseMarkdownFrontmatter };
